// file: src/components/layout/MainContent.jsx

import React, { useEffect, useState, useRef, useMemo } from 'react';
import Viewer from '../viewers/Viewer';
import { Columns2, Rows2 } from 'lucide-react';
import styles from './MainContent.module.css';
import { useViewerContext } from '../../context/ViewerContext';

// Import our custom components and hook
import useSplitView from '../../hooks/useSplitView';
import TabBar from '../tabs/TabBar';
import SplitView from './SplitView';

export default function MainContent({
sidebarCollapsed,
chatPanelCollapsed,
projectsExist,
className = '',
style = {}
}) {
// Get viewer state from context
const {
openFiles,
activeFileId,
jump,
closeFile,
setActiveFileId
} = useViewerContext();

// Fallback: if activeFileId is not set, choose the first file (if any)
const activeFile = openFiles.find(f => f.id === activeFileId) || openFiles[0] || null;

// Use our custom split view hook with properly separated file arrays
const splitView = useSplitView({
onTabSelect: setActiveFileId,
onTabClose: closeFile,
openFiles,
activeFileId,
excludePrimaryFromSecondary: true
});

// Compute the page to go to for the active file
const computeActivePageToGo = () => {
if (!activeFile) return undefined;

// If this is the cited file from context, use the citation page
if (jump && activeFile.id === jump.id) {
return jump.page;
}

return undefined;
};

// The single (normal) viewer for when no split is active
const renderSingleViewer = () => {
// Define the page number to show based on citation or default
const pageToGo = computeActivePageToGo();
// Pass the jump object to trigger navigation even for same page
const pageJump = jump && activeFile && jump.id === activeFile.id ? jump : null;

return (
<div className={styles.viewerArea}>
{activeFile ? (
<Viewer
file={activeFile}
pageToGo={pageToGo}
pageJump={pageJump}
key={`viewer-${activeFile.id}`}
/>
) : (
<p className={styles.emptyStateMessage}>
{!projectsExist
? "Create a project from the landing page to get started with your design process."
: sidebarCollapsed
? "Expand the left panel using the menu icon to access your project files."
: "Select a Document or Server from the left panel to view its content here."}
</p>
)}
</div>
);
};

// Generate the empty state message based on current state
const getEmptyStateMessage = () => {
if (!projectsExist) {
return "Create a project to view files here.";
}
return "No other file selected for this split view.";
};

  // Optimize className computation
  const containerClass = useMemo(() => [
    styles.mainContent,
    chatPanelCollapsed ? styles.noChatPanel : styles.withChatPanel,
    splitView.splitMode && styles.splitMode,
  ].filter(Boolean).join(' '), [chatPanelCollapsed, splitView.splitMode]);

  return (
    <div className={`${containerClass} ${className}`} style={style}>
      {/* The main tab bar - only show in non-split mode */}
      {!splitView.splitMode && (
        <div className={styles.tabsBar}>
          <div className={styles.tabsBarBackground} />
          <div className={styles.tabsScrollArea}>
            <TabBar
              files={openFiles}
              activeFileId={activeFileId}
              onTabSelect={splitView.handleSelectTab}
              onTabClose={splitView.handleCloseTab}
            />
          </div>

          {/* Split icons fixed on the right */}
          <div className={styles.splitIconsContainer}>
            {!splitView.splitMode && activeFile && (
              <div className={styles.splitToggleBox}>
                <div
                  className={styles.splitIconButton}
                  title="Split Vertically"
                  onClick={splitView.handleSplitVertical}
                >
                  <Rows2 size={18} strokeWidth={2} />
                </div>
                <div
                  className={styles.splitIconButton}
                  title="Split Horizontally"
                  onClick={splitView.handleSplitHorizontal}
                >
                  <Columns2 size={18} strokeWidth={2} />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Content area: either split view or single viewer */}
      {splitView.splitMode ? (
        <SplitView
          splitMode={splitView.splitMode}
          topPanelRef={splitView.topPanelRef}
          bottomPanelRef={splitView.bottomPanelRef}
          leftPanelRef={splitView.leftPanelRef}
          rightPanelRef={splitView.rightPanelRef}
          handleVerticalDrag={splitView.handleVerticalDrag}
          handleHorizontalDrag={splitView.handleHorizontalDrag}
          primaryFile={splitView.getPrimaryFile()}
          secondaryFile={splitView.getSecondaryFile()}
          secondaryFiles={splitView.getSecondaryFiles()}
          primaryFileId={splitView.splitPrimaryFileId}
          secondaryActiveId={splitView.splitSecondaryActiveId}
          primaryPageToGo={jump && splitView.getPrimaryFile()?.id === jump.id ? jump.page : undefined}
          primaryPageJump={jump && splitView.getPrimaryFile()?.id === jump.id ? jump : null}
          secondaryPageToGo={jump && splitView.getSecondaryFile()?.id === jump.id ? jump.page : undefined}
          secondaryPageJump={jump && splitView.getSecondaryFile()?.id === jump.id ? jump : null}
          onTabSelect={splitView.handleSelectTab}
          onTabClose={splitView.handleCloseTab}
          onExitSplit={splitView.handleExitSplit}
          emptyStateMessage={getEmptyStateMessage()}
        />
      ) : (
        renderSingleViewer()
      )}
    </div>
  );
}



